/**
 * Sawyer 14 early-clockin-gate (E1) — "Waiting for approval" on Punch records.
 *
 * Sawyer (memo2 [22:05]): "If somebody wants to check, like, a 10 minute
 * before, you had to get, like, approval". Every early clock-in still waiting
 * for a manager, from any day, with the two decisions named by the time they
 * produce: "Approve 8:40 AM" (pay from the real punch) or "Start at 9:00 AM"
 * (pay from the shift start). The punch itself is never rewritten.
 *
 * Design Philosophy:
 *  - P1 one glance: renders nothing when nothing waits (no empty box).
 *  - P2: both buttons are outline — "Add missed entry" stays the page's one
 *    filled primary.
 *  - P4: the pressed state is synchronous ("Saving…", both disabled); the
 *    card leaves only after the server's 200 (no optimistic removal).
 *  - P5: refusals show the server's own sentence; a card another manager
 *    already decided refreshes away instead of lingering (C11, stale state).
 *  - P7: 44px buttons, stacked full width at phone width.
 *  - Deploy window (AC21): `enabled` is false against a server without this
 *    lane, and a 403/404 from the list route hides the list silently.
 */
import { useCallback, useEffect, useState } from 'react';
import { adminTimeclockApi, type TimeclockEntry } from '../../lib/api.js';
import './early-clockin.css';

const STALE_CODE = 'early_not_pending';
const HIDDEN_CODES: ReadonlySet<string> = new Set(['forbidden', 'not_found', 'route_not_mapped']);

function fmtTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function sameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function fmtDay(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/** "Gabriel Martinez" → "Gabriel M." */
export function shortName(full: string): string {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'Unknown';
  if (parts.length === 1) return parts[0]!;
  return `${parts[0]} ${parts[parts.length - 1]![0]!.toUpperCase()}.`;
}

export function firstName(full: string): string {
  return full.trim().split(/\s+/)[0] || 'them';
}

type Decision = 'approve' | 'trim';

export type EarlyApprovalListProps = {
  apiUrl: string;
  /** False when the server does not carry this lane (AC21). */
  enabled: boolean;
  /** Bumped by the page to force a refetch (e.g. after a void). */
  refreshKey?: number;
  onDecided: (entry: TimeclockEntry, decision: Decision) => void;
  /** C11: another manager decided it first — the page notice explains. */
  onStale: (message: string) => void;
  /** Test seam: "today" for the day label. */
  now?: () => Date;
};

export function EarlyApprovalList({
  apiUrl,
  enabled,
  refreshKey = 0,
  onDecided,
  onStale,
  now = () => new Date(),
}: EarlyApprovalListProps): JSX.Element | null {
  const [entries, setEntries] = useState<TimeclockEntry[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hidden, setHidden] = useState(false);
  const [pending, setPending] = useState<{ id: string; decision: Decision } | null>(null);
  const [cardErrors, setCardErrors] = useState<Record<string, string>>({});

  const load = useCallback(async (): Promise<void> => {
    if (!enabled) return;
    setLoading(true);
    setLoadError(null);
    const res = await adminTimeclockApi.pendingEarly(apiUrl);
    setLoading(false);
    if (res.error !== undefined) {
      if (res.code !== undefined && HIDDEN_CODES.has(res.code)) {
        setHidden(true);
        return;
      }
      setLoadError(res.error);
      return;
    }
    setHidden(false);
    setEntries(res.data?.entries ?? []);
  }, [apiUrl, enabled]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const decide = async (entry: TimeclockEntry, decision: Decision): Promise<void> => {
    setPending({ id: entry.id, decision });
    setCardErrors((prev) => {
      const next = { ...prev };
      delete next[entry.id];
      return next;
    });
    const res = await adminTimeclockApi.decideEarly(apiUrl, entry.id, { decision });
    setPending(null);
    if (res.error !== undefined) {
      if (res.code === STALE_CODE) {
        onStale(res.error);
        void load();
        return;
      }
      setCardErrors((prev) => ({ ...prev, [entry.id]: res.error! }));
      return;
    }
    if (res.data) {
      setEntries((prev) => prev.filter((e) => e.id !== entry.id));
      onDecided(res.data.entry, decision);
    }
  };

  if (!enabled || hidden) return null;

  if (loading && entries.length === 0) {
    return (
      <section className="ferro-tc-early" aria-label="Waiting for approval" data-testid="tc-early-loading">
        <div className="ferro-skeleton ferro-tc-early__skeleton" />
      </section>
    );
  }

  if (loadError !== null) {
    return (
      <div
        className="ferro-alert ferro-alert--error ferro-alert--dismissible ferro-tc-early"
        role="alert"
        data-testid="tc-early-error"
      >
        <span>Couldn&rsquo;t load early clock-ins.</span>
        <button type="button" className="ferro-alert__dismiss" onClick={() => void load()}>
          Try again
        </button>
      </div>
    );
  }

  const waiting = entries.filter((e) => e.early?.status === 'pending' && e.voided_at === null);
  if (waiting.length === 0) return null;

  const today = now();
  return (
    <section className="ferro-tc-early" aria-label="Waiting for approval" data-testid="tc-early-list">
      <h2 className="ferro-tc-early__title">
        Waiting for approval
        <span className="ferro-tc-early__count" data-testid="tc-early-count">
          {waiting.length}
        </span>
      </h2>
      <ul className="ferro-tc-early__list">
        {waiting.map((entry) => {
          const early = entry.early!;
          const punch = fmtTime(entry.clock_in_at);
          const shift = fmtTime(early.scheduled_start_at);
          const mins = `${early.minutes_early} ${early.minutes_early === 1 ? 'minute' : 'minutes'}`;
          const isPending = pending?.id === entry.id;
          const err = cardErrors[entry.id];
          return (
            <li key={entry.id} className="ferro-tc-early__card" data-testid={`tc-early-${entry.id}`}>
              <p className="ferro-tc-early__text">
                <strong>{shortName(entry.staff_name)}</strong> clocked in <strong>{punch}</strong> for a{' '}
                <strong>{shift}</strong> shift ({mins} early)
                {sameLocalDay(new Date(entry.clock_in_at), today) ? null : (
                  <span className="ferro-tc-early__day">{fmtDay(entry.clock_in_at)}</span>
                )}
              </p>
              <div className="ferro-tc-early__actions">
                <button
                  type="button"
                  className="ferro-btn ferro-btn--outline"
                  disabled={pending !== null}
                  aria-busy={isPending && pending?.decision === 'approve'}
                  data-testid={`tc-early-approve-${entry.id}`}
                  onClick={() => void decide(entry, 'approve')}
                >
                  {isPending && pending?.decision === 'approve' ? 'Saving…' : `Approve ${punch}`}
                </button>
                <button
                  type="button"
                  className="ferro-btn ferro-btn--outline"
                  disabled={pending !== null}
                  aria-busy={isPending && pending?.decision === 'trim'}
                  data-testid={`tc-early-trim-${entry.id}`}
                  onClick={() => void decide(entry, 'trim')}
                >
                  {isPending && pending?.decision === 'trim' ? 'Saving…' : `Start at ${shift}`}
                </button>
              </div>
              {err ? (
                <p className="ferro-tc-early__error" role="alert" data-testid={`tc-early-error-${entry.id}`}>
                  {err}
                </p>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default EarlyApprovalList;
